import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Switch } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WhatshotRoundedIcon from '@mui/icons-material/WhatshotRounded';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'Components/RoadmapComponents/Menu';
import {
  communityCelebrations,
  communityMembers,
  getCurrentMemberRank,
  weeklyChallenge,
} from './communityPreviewData';
import './Community.css';

const Community = () => {
  const navigate = useNavigate();
  const [pointsOpen, setPointsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [sentKudos, setSentKudos] = useState<Record<string, boolean>>({});
  const [participating, setParticipating] = useState(true);
  const [discoverable, setDiscoverable] = useState(false);
  const [publicSharing, setPublicSharing] = useState(false);

  const currentRank = getCurrentMemberRank();
  const currentMember = communityMembers.find((member) => member.isCurrentUser);

  const toggleKudos = (celebrationId: string) => {
    setSentKudos((current) => ({ ...current, [celebrationId]: !current[celebrationId] }));
  };

  return (
    <div className="community-shell" data-testid="community-page">
      <Menu selected="Community" collapseToDrawer />

      <main className="community-page">
        <header className="community-header">
          <h1>Community</h1>
          <div className="community-header__meta">
            <span className="community-preview-pill">Concept preview · sample data</span>
            <button
              type="button"
              className="community-privacy-button"
              onClick={() => setPrivacyOpen(true)}
            >
              <LockRoundedIcon /> Private circle
            </button>
          </div>
        </header>

        <section className="community-overview" aria-labelledby="your-week-title">
          <article className="community-week-card" data-testid="community-week-card">
            <div className="community-card-heading">
              <h2 id="your-week-title">Your week</h2>
              <button
                type="button"
                className="community-icon-button"
                aria-label="Learn how Flow Points work"
                onClick={() => setPointsOpen(true)}
              >
                <InfoOutlinedIcon />
              </button>
            </div>

            <div className="community-week-score">
              <div
                className="community-score-ring"
                aria-label={`${currentMember?.flowPoints ?? 0} Flow Points`}
              >
                <div>
                  <strong>{currentMember?.flowPoints ?? 0}</strong>
                  <span>Flow Points</span>
                </div>
              </div>
              <div className="community-week-standing">
                <span>Circle rank</span>
                <strong>#{currentRank ?? '–'}</strong>
                <span className="community-positive">Up 3 this week</span>
              </div>
            </div>
          </article>

          <article className="community-challenge-card" data-testid="community-challenge-card">
            <div className="community-card-heading">
              <div>
                <span className="community-section-label">Weekly challenge</span>
                <h2>{weeklyChallenge.title}</h2>
              </div>
            </div>
            <p>{weeklyChallenge.description}</p>
            <div
              className="community-challenge-path"
              role="img"
              aria-label={`${weeklyChallenge.completedDays} of ${weeklyChallenge.targetDays} challenge days complete`}
            >
              {Array.from({ length: weeklyChallenge.targetDays }, (_, index) => {
                const isComplete = index < weeklyChallenge.completedDays;
                const isNext = index === weeklyChallenge.completedDays;
                return (
                  <div
                    className={`${isComplete ? 'is-complete' : ''} ${isNext ? 'is-next' : ''}`}
                    key={`challenge-day-${index + 1}`}
                  >
                    <span aria-hidden="true">
                      {isComplete ? <CheckCircleRoundedIcon /> : index + 1}
                    </span>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="community-primary-button"
              onClick={() => navigate('/roadmap')}
            >
              Continue practicing
              <ArrowForwardRoundedIcon aria-hidden="true" />
            </button>
            <span className="community-reward">Earn the {weeklyChallenge.reward}</span>
          </article>
        </section>

        <section className="community-grid">
          <article className="community-panel community-leaderboard" aria-labelledby="circle-title">
            <div className="community-panel-header">
              <h2 id="circle-title">Weekly Circle</h2>
              <span className="community-reset">Resets Sunday</span>
            </div>
            <div className="community-leaderboard-labels" aria-hidden="true">
              <span>Musician</span>
              <span>Flow Points</span>
            </div>
            <ol className="community-leaderboard-list">
              {communityMembers.map((member, index) => (
                <li
                  key={member.id}
                  className={`${member.isCurrentUser ? 'is-current-user' : ''} ${index < 3 ? 'is-podium' : ''}`}
                >
                  <span className={`community-rank community-rank--${index + 1}`}>{index + 1}</span>
                  <span className="community-avatar" style={{ backgroundColor: member.color }}>
                    {member.initials}
                  </span>
                  <span className="community-member-name">
                    <span className="community-member-name__line">
                      <strong>{member.alias}</strong>
                      {member.isCurrentUser && <em>You</em>}
                    </span>
                  </span>
                  <span className="community-member-points">{member.flowPoints}</span>
                </li>
              ))}
            </ol>
          </article>

          <aside className="community-side-column">
            <article
              className="community-panel community-celebrations"
              aria-labelledby="celebrations-title"
            >
              <div className="community-panel-header">
                <h2 id="celebrations-title">Celebrations</h2>
              </div>
              <div className="community-celebration-list">
                {communityCelebrations.map((celebration) => (
                  <div className="community-celebration" key={celebration.id}>
                    <span
                      className="community-avatar community-avatar--small"
                      style={{ backgroundColor: celebration.color }}
                    >
                      {celebration.initials}
                    </span>
                    <div>
                      <p>
                        <strong>{celebration.alias}</strong> {celebration.message}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label={`${sentKudos[celebration.id] ? 'Remove' : 'Send'} kudos to ${celebration.alias}`}
                      className={sentKudos[celebration.id] ? 'has-kudos' : undefined}
                      onClick={() => toggleKudos(celebration.id)}
                    >
                      <FavoriteRoundedIcon />
                      {sentKudos[celebration.id] ? 'Sent' : 'Kudos'}
                    </button>
                  </div>
                ))}
              </div>
            </article>
          </aside>
        </section>
      </main>

      <Dialog
        open={pointsOpen}
        onClose={() => setPointsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: 'community-dialog' }}
      >
        <DialogTitle>
          How Flow Points work
          <IconButton aria-label="Close" onClick={() => setPointsOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p>Flow Points reward steady learning—not endless practice or raw accuracy.</p>
          <ul className="community-points-list">
            <li>
              <WhatshotRoundedIcon />
              <span>
                <strong>Meaningful practice</strong>
                <small>Active time is verified and capped each day.</small>
              </span>
            </li>
            <li>
              <CheckCircleRoundedIcon />
              <span>
                <strong>Consistency</strong>
                <small>Build a healthy habit across multiple days.</small>
              </span>
            </li>
            <li>
              <MusicNoteRoundedIcon />
              <span>
                <strong>First-time progress</strong>
                <small>Advance a tier, level, or repertoire piece.</small>
              </span>
            </li>
            <li>
              <TrendingUpRoundedIcon />
              <span>
                <strong>Personal bests</strong>
                <small>Improve on comparable material at your level.</small>
              </span>
            </li>
          </ul>
        </DialogContent>
      </Dialog>

      <Dialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: 'community-dialog' }}
      >
        <DialogTitle>
          My community privacy
          <IconButton aria-label="Close" onClick={() => setPrivacyOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p>Your circle sees only your generated alias, avatar, and weekly progress.</p>
          <p>
            These preview controls are stored only for this screen and are not connected to an
            account yet.
          </p>
          <div className="community-privacy-controls">
            <label>
              <span>
                <strong>Join my Weekly Circle</strong>
                <small>Show my alias and weekly progress to my private circle.</small>
              </span>
              <Switch
                checked={participating}
                onChange={(_, checked) => setParticipating(checked)}
              />
            </label>
            <label>
              <span>
                <strong>Let people I know find me</strong>
                <small>Off by default. Future friend and family circles only.</small>
              </span>
              <Switch checked={discoverable} onChange={(_, checked) => setDiscoverable(checked)} />
            </label>
            <label>
              <span>
                <strong>Allow public achievement links</strong>
                <small>Create revocable, unlisted pages only when you choose to share.</small>
              </span>
              <Switch
                checked={publicSharing}
                onChange={(_, checked) => setPublicSharing(checked)}
              />
            </label>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Community;
